<template>
  <div ref="rootEl" class="w-full pt-1">
    <div class="relative h-4">
      <span
        v-for="(label, i) in labels"
        :key="'l' + i"
        class="absolute top-0 whitespace-nowrap text-[12px] leading-[16px] text-gray-300 tabular-nums"
        :style="
          label.last
            ? { right: '0px' }
            : label.first
              ? { left: '0px' }
              : { left: label.pct + '%', transform: 'translateX(-50%)' }
        "
      >
        {{ label.text }}
      </span>
    </div>

    <div class="relative h-2">
      <span
        v-for="(tick, i) in ticks"
        :key="'t' + i"
        class="absolute bottom-0 w-px bg-gray-300"
        :class="tick.tall ? 'h-2' : 'h-1'"
        :style="
          tick.pct <= 0
            ? { left: '0px' }
            : tick.pct >= 100
              ? { right: '0px' }
              : { left: tick.pct + '%', transform: 'translateX(-50%)' }
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  duration: number
}>()

const rootEl = ref<HTMLElement>()
const width = ref(0)
let resizeObserver: ResizeObserver | null = null

const formatTime = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds))
  const mm = String(Math.floor(s / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

const tallCount = computed(() => {
  if (width.value < 1) return 0
  return Math.max(2, Math.round(width.value / 28) + 1)
})

const labelCount = computed(() => {
  const intervals = tallCount.value - 1
  if (!props.duration || intervals < 1) return 0
  const target = Math.max(2, Math.min(13, Math.round(width.value / 100) + 1))
  return Math.min(target, intervals + 1)
})

const labels = computed(() => {
  const n = labelCount.value
  const intervals = tallCount.value - 1
  if (n < 2) return []
  const out: Array<{
    text: string
    pct: number
    first: boolean
    last: boolean
  }> = []
  for (let i = 0; i < n; i++) {
    const frac = Math.round((i * intervals) / (n - 1)) / intervals
    out.push({
      text: formatTime(frac * props.duration),
      pct: frac * 100,
      first: i === 0,
      last: i === n - 1,
    })
  }
  return out
})

const ticks = computed(() => {
  const count = tallCount.value
  if (count < 2) return []
  const out: Array<{ pct: number; tall: boolean }> = []
  for (let j = 0; j < count; j++) {
    out.push({ pct: (j / (count - 1)) * 100, tall: true })
    if (j < count - 1) {
      for (let k = 1; k <= 4; k++) {
        out.push({ pct: ((j + k / 5) / (count - 1)) * 100, tall: false })
      }
    }
  }
  return out
})

onMounted(() => {
  if (!rootEl.value) return
  resizeObserver = new ResizeObserver(([entry]) => {
    width.value = entry.contentRect.width
  })
  resizeObserver.observe(rootEl.value)
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>
